import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  editCompareGroup,
  editCorrelations,
  editSurvival,
  editMixedMuktivariate,
  editMultivariate,
} from "../../actions/analyses";
import { Drawer, Grid, Typography, Box, Backdrop, CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { EditForm } from "./editForm";
import { isEqual } from "lodash";
import { ANALYSES_TYPES } from "constants/common.constants";

const SINGLE_MODEL_TYPES = [
  ANALYSES_TYPES.MULTIVARIATE_SINGLE,
  ANALYSES_TYPES.MIXED_MODEL_SINGLE,
  ANALYSES_TYPES.TREE_ANALYSIS,
];

export const EditGraph = ({ open, handleClose, route, successCallback = () => {} }) => {
  const [loader, setLoader] = useState(false);
  const [configuration, setConfiguration] = useState(null);
  const dispatch = useDispatch();
  const analyse = useSelector((state) => state.analysesReducer.analyse);
  const selectGraphImage = useSelector((state) => state.analysesReducer.selectGraphImage);

  const confRef = useRef(null);

  useEffect(() => {
    if (analyse.summary?.data && route) {
      if (route[1] === "heatmap" && analyse.analysis_type === "correlations") {
        return setConfiguration(analyse.summary.data[route[0]].configurationHeat[0]);
      }

      if (analyse.analysis_type === "frequency_tables") {
        return setConfiguration(analyse.summary.data[route[0]].configurations[route[1]]);
      }

      if (analyse.analysis_type === "mixed_multivariate") {
        if (route.length > 2) {
          setConfiguration(analyse.summary.data[route[0]].configurations.all_models[route[1]][route[2]][0]);
        } else {
          setConfiguration(analyse.summary.data[route[0]].configurations.main_independents[route[1]][0]);
        }
        return;
      }

      if (analyse.analysis_type === "multivariate") {
        if (route.length > 2) {
          setConfiguration(analyse.summary.data[route[0]].configurations.all_models[route[1]][0][route[2]]);
        } else {
          setConfiguration(analyse.summary.data[route[0]].configurations.main_independents[route[1]][0]);
        }
        return;
      }

      if (analyse.analysis_type === ANALYSES_TYPES.MULTIVARIATE_SINGLE)
        return setConfiguration(analyse.summary.data[route[0]].configuration[route[1]].singlemodel);

      if (
        analyse.analysis_type === ANALYSES_TYPES.MIXED_MODEL_SINGLE ||
        analyse.analysis_type === ANALYSES_TYPES.TREE_ANALYSIS
      )
        return setConfiguration(analyse.summary.data[route[0]].configurations[route[1]].singlemodel);

      setConfiguration(analyse.summary.data[route[0]].configurations[route[1]][route[2]][0]);
    }
  }, [analyse, route]);

  const onChange = (event) => {
    let _configuration = { ...configuration };
    _configuration = {
      ...configuration,
      [event.target.name]: event.target.value || event.target.checked,
    };
    setConfiguration(_configuration);
  };

  useEffect(() => {
    if (isEqual(configuration, confRef.current)) return;
    if (configuration) {
      confRef.current = configuration;
      handleSubmit("single", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration]);

  const handleSubmit = (apply_term, save) => {
    if (save) setLoader(true);
    let data = {
      file_configuration: { width: 1000, height: 1000, resolution: 200 },
    };
    if (analyse.analysis_type === "compare_groups" || analyse.analysis_type === "dependent") {
      data = {
        ...data,
        data: {
          name: analyse.name,
          analysis_id: analyse.id,
          apply_term: apply_term,
          split_level: [route[0]],
          save,
          var1: [route[1]],
          var2: [route[2]],
          new_configuration: [configuration],
        },
      };
      dispatch(editCompareGroup({ data, route, callback: setLoader, successCallback }));
    }
    if (analyse.analysis_type === "correlations") {
      data = {
        ...data,
        data: {
          name: analyse.name,
          analysis_id: analyse.id,
          apply_term: apply_term,
          split_level: [route[0]],
          save: save,
          var1: route[1] === "heatmap" ? [] : [route[1]],
          var2: route[1] === "heatmap" ? [] : [route[2]],
          new_configuration: [configuration],
        },
      };
      dispatch(editCorrelations({ data, route, callback: setLoader, successCallback }));
    }

    if (analyse.analysis_type === "survival") {
      data = {
        ...data,
        data: {
          name: analyse.name,
          analysis_id: analyse.id,
          apply_term: apply_term,
          split_level: route[0] === "all" ? "all" : route[0],
          save: save,
          var1: route[1],
          var2: route[2],
          new_configuration: [configuration],
        },
      };
      dispatch(editSurvival({ data, route, callback: setLoader, successCallback }));
    }

    if (analyse.analysis_type === "multivariate") {
      data = {
        ...data,
        data: {
          name: analyse.name,
          analysis_id: analyse.id,
          apply_term: apply_term,
          split_level: route[0] === "all" ? ["all"] : [route[0]],
          save: save,
          var1: [route[1]],
          var2: route[2] ? [route[2]] : [],
          new_configuration: [configuration],
        },
      };
      dispatch(editMultivariate({ data, route, callback: setLoader, successCallback }));
    }

    if (SINGLE_MODEL_TYPES.includes(analyse.analysis_type)) {
      data = {
        ...data,
        data: {
          name: analyse.name,
          analysis_id: analyse.id,
          apply_term: apply_term,
          split_level: route[0] === "all" ? ["all"] : [route[0]],
          save: save,
          var1: [route[1]],
          var2: [],
          new_configuration: [configuration],
        },
      };
      dispatch(editMultivariate({ data, route, callback: setLoader, successCallback }));
    }

    if (analyse.analysis_type === "mixed_multivariate") {
      data = {
        ...data,
        data: {
          name: analyse.name,
          analysis_id: analyse.id,
          apply_term: apply_term,
          split_level: route[0] === "all" ? "all" : route[0],
          save: save,
          var1: [route[1]],
          var2: route[2] ? [route[2]] : [],
        },
        configuration: [configuration],
      };
      dispatch(editMixedMuktivariate({ data, route, callback: setLoader, successCallback }));
    }
  };

  const getImageUrl = () => {
    if (selectGraphImage) return selectGraphImage;

    if (analyse.analysis_type === "correlations" && route[1] === "heatmap")
      return analyse?.summary?.images[route[0]].heatmap;

    if (analyse.analysis_type === "frequency_tables")
      return analyse?.summary?.images[route[0]].main_independents[route[1]];

    if (SINGLE_MODEL_TYPES.includes(analyse.analysis_type))
      return analyse?.summary?.images[route[0]][route[1]].singlemodel;

    if (analyse.analysis_type === "mixed_multivariate" || analyse.analysis_type === "multivariate") {
      if (route.length > 2) {
        return analyse?.summary?.images[route[0]].all_models[route[1]][route[2]];
      } else {
        return analyse?.summary?.images[route[0]].main_independents[route[1]];
      }
    }

    return analyse?.summary?.images[route[0]][route[1]][route[2]];
  };

  return (
    <>
      {!!configuration && (
        <Drawer
          anchor="right"
          open={open}
          onClose={() => handleClose()}
          sx={{ zIndex: 1300 }}
          PaperProps={{
            sx: { width: "calc(100vw - 200px)", background: "#F8FAFF" },
          }}
        >
          <Grid container justifyContent={"flex-end"} wrap={"nowrap"}>
            <Grid item width={"100%"}>
              <Box
                sx={{
                  padding: "50px 40px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ExpandMoreIcon
                  sx={{
                    color: "#141414",
                    cursor: "pointer",
                    transform: "rotate(.25turn)",
                    marginRight: "15px",
                  }}
                  onClick={() => handleClose()}
                />
                <Typography variant="h2" color="#141414">
                  Graphic View
                </Typography>
              </Box>
              <Box
                sx={{
                  padding: "0 40px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <img
                  style={{ maxWidth: "100%", maxHeight: "calc(100vh - 300px)" }}
                  src={getImageUrl()}
                  alt="graph"
                />
              </Box>
            </Grid>
            <Grid item minWidth="400px">
              <EditForm
                configuration={configuration}
                onChange={onChange}
                handleSubmit={handleSubmit}
                handleClose={handleClose}
                analyseType={analyse.analysis_type}
                itHeat={route[1] === "heatmap" && analyse.analysis_type === "correlations"}
              />
            </Grid>
          </Grid>
          <Backdrop sx={{ color: "#fff", zIndex: 10001 }} open={loader}>
            <Box display={"flex"} flexDirection="column" alignItems={"center"}>
              <Typography>Updating graphs</Typography>
              <CircularProgress color="inherit" />
            </Box>
          </Backdrop>
        </Drawer>
      )}
    </>
  );
};
